import React from 'react'
import {
  MinimalGlobalStyles,
  MinimalThemeProvider,
  createMinimalTheme,
  type DeepPartial,
  type MinimalTheme,
} from '@ovasabi/ui-minimal'
import './appReset'

/** Brand tokens shared by both editions. Nunito follows the rounded source lettering. */
export const appTheme: DeepPartial<MinimalTheme> = {
  name: 'chow',
  color: {
    // Pure white canvas as required by design system.
    bgApp: '#ffffff',
    bgSurface: '#ffffff',
    bgSurfaceAlt: '#f8f9fa',
    bgSurfaceHover: '#f1f3f5',
    bgElevated: '#ffffff',
    bgOverlay: 'rgba(23, 39, 62, 0.56)',
    // Logo navy ink (sampled from the mascot's wok and wordmark).
    textPrimary: '#17273E',
    textSecondary: '#4A5868',
    /* Tertiary ink is used at 11–13px (captions, chef names, meta lines). */
    textTertiary: '#616C7A',
    textInverse: '#ffffff',
    borderSubtle: '#e2e8f0',
    borderStrong: '#cbd5e1',
    borderFocus: '#17273E',
    // Logo crimson — the ChooseChow brand accent (see DESIGN.md color contract).
    // 4.6:1 against white, so white button labels on it still clear WCAG AA.
    brand: '#E61445',
    brandSoft: 'rgba(230, 20, 69, 0.11)',
    // Leafy green for confirmations and earnings.
    success: '#2f8f4e',
    successSoft: 'rgba(47, 143, 78, 0.13)',
    // Golden plantain for caution / expiring offers.
    warning: '#e0a008',
    warningSoft: 'rgba(224, 160, 8, 0.16)',
    danger: '#c12926',
    dangerSoft: 'rgba(193, 41, 38, 0.12)',
    info: '#2f6fed',
    infoSoft: 'rgba(47, 111, 237, 0.12)',
  },
  typography: {
    // Original artwork carries the wordmark; Nunito carries interface text.
    displayFamily: '"Nunito", "Instrument Sans", -apple-system, sans-serif',
    bodyFamily: '"Nunito", "Instrument Sans", -apple-system, sans-serif',
    displaySize: 'clamp(2.5rem, 5vw, 3.75rem)',
    h1Size: 'clamp(1.75rem, 3vw, 2.5rem)',
    // Section/card headers. One token drives every SectionHeading + modal title,
    // so peer-level headers share exactly one size app-wide (see SectionHeading).
    h2Size: '1.375rem',
    bodySize: '1rem',
    captionSize: '0.8125rem',
    metaSize: '0.75rem',
    lineHeightTight: 1.08,
    lineHeightBody: 1.6,
  },
  radius: {
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    pill: '999px',
  },
  // Rich layered elevation with warm ambient contact shadow and high depth perception.
  shadow: {
    subtle: '0 2px 8px rgba(23, 39, 62, 0.04)',
    medium: '0 8px 24px rgba(23, 39, 62, 0.08)',
    floating: '0 8px 16px rgba(23, 39, 62, 0.08), 0 28px 56px -10px rgba(23, 39, 62, 0.24)',
  },
}


export const theme = createMinimalTheme(appTheme)
export const GlobalStyles = () => React.createElement(MinimalGlobalStyles, null)
/**
 * The app's styles are Linaria like ui-minimal's, so they read tokens through
 * `minimalVars` (CSS variables); an edition's theme reaches them through the
 * variables MinimalGlobalStyles sets, so editions still restain at runtime.
 */
export const AppThemeProvider = MinimalThemeProvider
export type Theme = MinimalTheme

